import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { Card, Divider, Grid, Typography, Box, Stack, Container } from '@mui/material';

import Routes from 'constants/routes';
import { removeStaff, getStaffList } from 'redux/slices/staff/staffSlice';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { fetchStaff, type StaffMember } from 'services/staffApi';
import { hasAtLeastRole } from 'api/v1Helpers';
import { fetchMember } from 'services/membersApi';
import Button from 'components/Common/Button';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import DetailItem from 'components/Staff/ItemDetail';
import Profile from 'components/Staff/Profile';
import Modal from 'components/Common/Modal';
import MuiLoader from 'components/Common/Loader';

/**
 * Staff detail is Phase 5 (/v1 staff list). Non-staff path uses /v1 members (Phase 12 cleanup).
 */
export default function UserDetail() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const [openModal, setOpenModal] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [staffDetails, setStaffDetails] = useState<StaffMember | null>(null);
    const [memberDetails, setMemberDetails] = useState<Awaited<ReturnType<typeof fetchMember>> | null>(null);
    const { username: routeParam } = params || {};
    const { type } = location?.state || {};
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageStaff = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');

    const isStaff = type === 'employee';

    useEffect(() => {
        if (!routeParam) return;
        if (!parishId) {
            setInitialLoading(true);
            setStaffDetails(null);
            setMemberDetails(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setInitialLoading(true);
            try {
                if (isStaff) {
                    const res = await fetchStaff(parishId);
                    if (cancelled) return;
                    setStaffDetails(res.items.find((s) => s.user.id === routeParam) ?? null);
                    setMemberDetails(null);
                } else {
                    const member = await fetchMember(parishId, routeParam);
                    if (cancelled) return;
                    setMemberDetails(member);
                    setStaffDetails(null);
                }
            } catch {
                if (!cancelled) {
                    if (isStaff) setStaffDetails(null);
                    else setMemberDetails(null);
                }
            }
            if (!cancelled) setInitialLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [routeParam, parishId, isStaff]);

    const user = isStaff ? staffDetails?.user : null;
    const displayName = isStaff ? [user?.firstName, user?.lastName].filter(Boolean).join(' ') : memberDetails?.name || '';
    const displayEmail = isStaff ? user?.email || '' : memberDetails?.email || memberDetails?.username || '';
    const displayFirstName = isStaff ? user?.firstName || '' : memberDetails?.firstName || '';
    const displayLastName = isStaff ? user?.lastName || '' : memberDetails?.lastName || '';
    const displayRole = isStaff ? staffDetails?.role || '' : '';

    const handleRemove = useCallback(async () => {
        if (!routeParam || !parishId) return;
        setLoading(true);
        const res = await dispatch(removeStaff({ parishId, userId: routeParam }));
        setLoading(false);
        if (!(res as any)?.error) {
            setOpenModal(false);
            dispatch(getStaffList({ parishId }));
            navigate(Routes.staff.main);
        }
    }, [dispatch, routeParam, parishId, navigate]);

    const profileData = useMemo(
        () => ({
            name: displayName,
            email: displayEmail,
            logoUrl: isStaff ? user?.avatarUrl || '' : memberDetails?.imgUrl || '',
        }),
        [displayName, displayEmail, isStaff, user, memberDetails]
    );

    return (
        <Container>
            <Card sx={{ width: '100%', p: '2rem', my: 1 }}>
                <Stack m={'auto'} width={{ xs: '100%', md: '80%' }}>
                    <MuiLoader absolute loading={initialLoading} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                        <Typography sx={{ fontSize: 16, color: '#1F2937', fontWeight: 400 }} variant="h1">
                            {isStaff ? 'Staff Member' : 'User Detail'}
                        </Typography>
                    </Box>

                    <Divider />
                    <Profile my={6} user={profileData} />
                    <Grid container>
                        <Grid item xs={6}>
                            <DetailItem label="First Name" value={displayFirstName} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailItem label="Email" value={displayEmail} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailItem label="Last Name" value={displayLastName} />
                        </Grid>
                        {isStaff && (
                            <Grid item xs={6}>
                                <DetailItem label="Role" value={displayRole} />
                            </Grid>
                        )}
                    </Grid>
                    {isStaff && staffDetails && canManageStaff && (
                        <Stack justifyContent={'center'} direction={'row'} mt={5}>
                            <Button
                                sx={{ width: { xs: '100%', sm: '45%' }, fontSize: { xs: 16, xl: 20 } }}
                                onClick={() => setOpenModal(true)}
                                title="Remove Staff Member"
                            />
                        </Stack>
                    )}

                    <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
                        <>
                            <Typography sx={{ fontSize: 18, my: 2 }}>
                                Once you remove this staff member they will no longer be able to access the portal for this parish.
                                <br />
                                Are you sure you would like to remove this staff member?
                            </Typography>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                                <Button onClick={() => setOpenModal(false)} width={'45%'} title="Cancel" />
                                <Button loading={loading} onClick={handleRemove} width={'45%'} title="Confirm" />
                            </div>
                        </>
                    </Modal>
                    <AssistanceFooter />
                </Stack>
            </Card>
        </Container>
    );
}
